import { PaymentMethodDto } from '@/services/platform/api/model';

/**
 * Stripe returns card brands as lowercase slugs (`visa`, `mastercard`, `amex`, `unionpay`).
 * The design shows them title-cased, with the multi-word brands spelled out properly.
 */
const BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  maestro: 'Maestro',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

/** Maps a Stripe brand slug to its display name, falling back to capitalising the slug. */
export function formatCardBrand(brand: string | null | undefined): string {
  if (!brand) return '';
  const key = brand.toLowerCase();
  return BRAND_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Formats a card's expiry as `MM/YYYY`, matching the design ("Exp 02/2028").
 *
 * Returns `undefined` when either part is missing so callers can omit the line entirely
 * rather than rendering a half-empty date.
 */
export function formatExpiry(
  expMonth: number | undefined,
  expYear: number | undefined
): { month: string; year: string } | undefined {
  if (!expMonth || !expYear) return undefined;
  return { month: String(expMonth).padStart(2, '0'), year: String(expYear) };
}

/** Pre-fills the edit form's MM/YY field from a saved card. */
export function toExpiryInput(paymentMethod: PaymentMethodDto): string {
  const { expMonth, expYear } = paymentMethod;
  if (!expMonth || !expYear) return '';
  return `${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`;
}

/**
 * Masks free text into `MM/YY` as the user types.
 *
 * Mirrors the digit-slice approach used by the account-creation date field
 * (`splashCreateAccountStep.tsx`) — the project has no input-mask library.
 */
export function maskExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/** Why an `MM/YY` input can't be submitted, or `null` when it is valid. */
export type ExpiryError = 'incomplete' | 'invalid-month' | 'past';

/**
 * Parses an `MM/YY` input into the numeric parts the API expects.
 *
 * Two-digit years are read as 2000-based, matching how the field is presented.
 *
 * Rejects expiries in the past, mirroring the API's `HasValidExpiry` check
 * (`PaymentMethodService.cs`) — a card expiring earlier this month or in a past year is refused
 * server-side with `400 {"error":"Invalid expiry"}`, so catching it here keeps the CTA honest
 * instead of letting the user submit a request that cannot succeed. The current month is treated
 * as still valid, matching the backend's `<` comparisons.
 *
 * @param now - Injectable clock for tests; defaults to the real current date.
 */
export function parseExpiry(
  value: string,
  now: Date = new Date()
): { value?: { expMonth: number; expYear: number }; error?: ExpiryError } {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 4) return { error: 'incomplete' };

  const expMonth = Number(digits.slice(0, 2));
  const expYear = 2000 + Number(digits.slice(2));

  if (expMonth < 1 || expMonth > 12) return { error: 'invalid-month' };

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
    return { error: 'past' };
  }

  return { value: { expMonth, expYear } };
}
